import React from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTranslation } from 'react-i18next';

import { useLocalizationStyles } from './styles';
import { ISpotLocalization } from './types';

interface VideoOverrideCardProps {
  code: string;
  languageName: string;
  override: ISpotLocalization;
  isConverting?: boolean;
  isActive?: boolean;
  onUploadVideo: (code: string) => void;
  onReplaceVideo: (code: string) => void;
  onUploadPreview: (code: string) => void;
  onReplacePreview: (code: string) => void;
  onUseDefaultPreview: (code: string) => void;
  onPreview: (code: string) => void;
  onRemove: (code: string) => void;
}

const filenameFromUrl = (raw: string | undefined): string => {
  if (!raw) {
    return '';
  }
  try {
    const parsed = new URL(raw, window.location.href);
    return decodeURIComponent(parsed.pathname.split('/').pop() || raw);
  } catch {
    return decodeURIComponent(raw.split('/').pop() || raw);
  }
};

interface VideoStatus {
  text: string;
  cls: 'ok' | 'warn' | 'error';
}

const useVideoStatus = (
  hasVideo: boolean,
  isConverting: boolean,
  hasPreview: boolean,
): VideoStatus => {
  const { t: translate } = useTranslation();

  if (!hasVideo) {
    return {
      text: translate('editorMode.localizationNoVideo', 'Video not selected'),
      cls: 'warn',
    };
  }
  if (isConverting) {
    return {
      text: translate(
        'editorMode.localizationConverting',
        'Converting to WebM…',
      ),
      cls: 'warn',
    };
  }
  if (!hasPreview) {
    return {
      text: translate(
        'editorMode.localizationPreviewDefault',
        'Using default preview',
      ),
      cls: 'ok',
    };
  }
  return {
    text: translate('editorMode.localizationReady', 'Ready · WebM + preview'),
    cls: 'ok',
  };
};

const VideoOverrideCard = ({
  code,
  languageName,
  override,
  isConverting = false,
  isActive = false,
  onUploadVideo,
  onReplaceVideo,
  onUploadPreview,
  onReplacePreview,
  onUseDefaultPreview,
  onPreview,
  onRemove,
}: VideoOverrideCardProps) => {
  const { t: translate } = useTranslation();
  const styles = useLocalizationStyles();

  const videoSrc = override.url || override.location;
  const videoFilename = override.filename || filenameFromUrl(videoSrc);
  const previewSrc = override.previewImage;
  const previewFilename =
    override.previewFilename || filenameFromUrl(previewSrc);

  const hasVideo = Boolean(videoFilename || videoSrc);
  const hasPreview = Boolean(previewFilename || previewSrc);
  const status = useVideoStatus(hasVideo, isConverting, hasPreview);
  const statusStyle =
    status.cls === 'ok'
      ? styles.statusOk
      : status.cls === 'error'
      ? styles.statusError
      : styles.statusWarn;

  const isVideoReady = hasVideo && !isConverting;
  const showPreviewRow = !isConverting;

  return (
    <Box sx={styles.videoCard}>
      <Box sx={styles.videoCardHeader}>
        <Typography
          component="span"
          sx={[styles.langCode, ...(isActive ? [styles.langCodeActive] : [])]}
        >
          {code}
        </Typography>
        <Typography
          component="span"
          sx={[styles.langName, { flex: 1, width: 'auto' }]}
        >
          {languageName}
        </Typography>
        {isActive && (
          <Typography component="span" sx={styles.activeBadge}>
            {translate('editorMode.localizationActiveBadge', 'Active')}
          </Typography>
        )}
        {isVideoReady && (
          <Tooltip
            title={translate('editorMode.viewVideo', 'View video')}
            placement="bottom"
            enterDelay={400}
          >
            <IconButton
              size="small"
              sx={styles.iconButton}
              onClick={() => onPreview(code)}
              aria-label={translate('editorMode.viewVideo', 'View video')}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={translate(
            'editorMode.localizationRevert',
            'Revert to default',
          )}
          placement="bottom"
          enterDelay={400}
        >
          <IconButton
            size="small"
            sx={[styles.iconButton, styles.iconButtonDanger]}
            onClick={() => onRemove(code)}
            aria-label={translate(
              'editorMode.localizationRevert',
              'Revert to default',
            )}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={styles.videoCardLine}>
        <Box sx={styles.thumbSm}>
          <Box sx={styles.playBadge}>
            <PlayArrowOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
        </Box>
        <Box sx={styles.videoCardMeta}>
          {hasVideo ? (
            <Tooltip title={videoFilename} placement="bottom" enterDelay={400}>
              <Typography sx={styles.videoCardTitle}>
                {videoFilename}
              </Typography>
            </Tooltip>
          ) : (
            <Typography sx={[styles.videoCardTitle, styles.statusWarn]}>
              {translate(
                'editorMode.localizationNoVideo',
                'Video not selected',
              )}
            </Typography>
          )}
          <Typography sx={[styles.videoCardStatus, statusStyle]}>
            {status.text}
          </Typography>
          {isConverting && (
            <LinearProgress variant="indeterminate" sx={styles.conversionBar} />
          )}
        </Box>
        {hasVideo && !isConverting ? (
          <Tooltip
            title={translate('editorMode.replace', 'Replace')}
            placement="bottom"
            enterDelay={400}
          >
            <IconButton
              size="small"
              sx={styles.iconButton}
              onClick={() => onReplaceVideo(code)}
              aria-label={translate('editorMode.replace', 'Replace')}
            >
              <FileUploadOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        ) : !hasVideo ? (
          <Button
            size="small"
            variant="contained"
            sx={styles.miniUpload}
            onClick={() => onUploadVideo(code)}
            startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 14 }} />}
          >
            {translate('editorMode.video', 'Video')}
          </Button>
        ) : null}
      </Box>

      {showPreviewRow && (
        <Box sx={styles.videoCardLine}>
          <Box
            sx={styles.thumbXs}
            style={
              hasPreview && previewSrc
                ? { backgroundImage: `url("${previewSrc}")` }
                : undefined
            }
          >
            {!(hasPreview && previewSrc) && (
              <ImageOutlinedIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
          <Box sx={styles.videoCardMeta}>
            <Typography sx={styles.videoCardTitle}>
              {translate('editorMode.previewImage', 'Preview image')}
            </Typography>
            {hasPreview ? (
              <Tooltip
                title={previewFilename}
                placement="bottom"
                enterDelay={400}
              >
                <Typography sx={[styles.videoCardStatus, styles.statusOk]}>
                  {previewFilename}
                </Typography>
              </Tooltip>
            ) : (
              <Typography sx={[styles.videoCardStatus, styles.statusOk]}>
                {translate(
                  'editorMode.localizationPreviewDefault',
                  'Using default preview',
                )}
              </Typography>
            )}
          </Box>
          <Box sx={styles.actions}>
            {hasPreview && (
              <Tooltip
                title={translate(
                  'editorMode.localizationUseDefaultPreview',
                  'Use default preview',
                )}
                placement="bottom"
                enterDelay={400}
              >
                <IconButton
                  size="small"
                  sx={styles.iconButton}
                  onClick={() => onUseDefaultPreview(code)}
                  aria-label={translate(
                    'editorMode.localizationUseDefaultPreview',
                    'Use default preview',
                  )}
                >
                  <RestartAltOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
            {hasPreview ? (
              <Tooltip
                title={translate('editorMode.replace', 'Replace')}
                placement="bottom"
                enterDelay={400}
              >
                <IconButton
                  size="small"
                  sx={styles.iconButton}
                  onClick={() => onReplacePreview(code)}
                  aria-label={translate('editorMode.replace', 'Replace')}
                >
                  <FileUploadOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                size="small"
                variant="contained"
                sx={styles.miniUpload}
                onClick={() => onUploadPreview(code)}
                startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 14 }} />}
              >
                {translate('editorMode.previewImage', 'Preview image')}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VideoOverrideCard;
